// Generated by tutorials/record.cjs from tutorials/articles/h33-phones-and-tablets.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h33 --text-only` (wording) or
// `node tutorials/record.cjs h33` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'phones-and-tablets',
  catalogId: 'h33',
  title: 'Using Dietech Online on a phone or tablet',
  summary: 'Dietech Online works in the web browser on a phone or tablet: no download. See how the phone layout works, what you can do there, and what is better done on a computer.',
  audience: 'Everyone who uses Dietech Online away from a desk',
  area: 'general',
  learn: [
    'Get around with the tab bar, More and the menu',
    'Find a resident and move around their record',
    'Take menu choices in Selective Touch, upright or sideways',
    'See today\'s menu and swap one item',
    'Run a report',
    'What a tablet shows, and what needs a computer',
  ],
  keywords: [
    'phone',
    'mobile',
    'cell phone',
    'smartphone',
    'tablet',
    'ipad',
    'android',
    'iphone',
    'touch',
    'tab bar',
    'bottom bar',
    'more',
    'menu button',
    'hamburger',
    'sideways',
    'landscape',
    'rotate',
    'today\'s menu',
    'swap an item',
    'browser',
    'app',
  ],
  steps: [
    {
      id: 'home',
      part: 'Getting around',
      heading: 'The tab bar',
      text: 'On a phone, the bar along the bottom takes you to the four places you use most: Home (your dashboard), Residents, Menus and Touch (Selective Touch). The tab you are in is lit.\n\nThe bar at the top keeps search, Help, your notifications, your facility and your name, as on a computer.',
      media: {
        image: {
          src: '/help/phones-and-tablets/home.webp',
          width: 584,
          height: 1266,
        },
        alt: 'The dashboard on a phone, with the tab bar at the bottom highlighted: Home, Residents, Menus, Touch and More.',
      },
    },
    {
      id: 'more',
      part: 'Getting around',
      heading: 'More',
      text: 'Tap More for everything else. Sections opens the other parts of Dietech Online, such as Clinical, Kitchen and Reports.\n\nUnder This session are your facility (tap it to switch), Start Support Session for when Dietech support asks you to share your screen, and Report a problem with this screen.',
      media: {
        image: {
          src: '/help/phones-and-tablets/more.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/more.mp4',
          width: 584,
          height: 1266,
          durationSec: 2.6,
        },
        alt: 'The More sheet open from the bottom: Sections with Clinical, Food Data, Kitchen, Purchasing, Reports and Setup, then This session with Maple Grove Care Center, Start Support Session and Report a problem with this screen.',
      },
    },
    {
      id: 'menu',
      part: 'Getting around',
      heading: 'The full menu',
      text: 'The button at the top left opens the menu you know from a computer, down the left side. Tap a section, such as Residents, to see its pages, then tap the page.\n\nTap outside the menu to close it.',
      media: {
        image: {
          src: '/help/phones-and-tablets/menu.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/menu.mp4',
          width: 584,
          height: 1266,
          durationSec: 4.2,
        },
        alt: 'The menu open over the dashboard, with Residents tapped on the left and its pages listed: Residents, Census, Guests, Weight Entry, Intake Studies, Selective Touch and Credits.',
      },
    },
    {
      id: 'find',
      part: 'Residents',
      heading: 'Find a resident',
      text: 'Tap Residents in the tab bar. Each resident is a card: name, unit, room and bed, diet order, and any flags.\n\nTap Search residents at the top and type a few letters of a name; the cards narrow as you type.',
      media: {
        image: {
          src: '/help/phones-and-tablets/find.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/find.mp4',
          width: 584,
          height: 1266,
          durationSec: 5.7,
        },
        alt: 'Residents on a phone with fat typed in Search residents and one card left: Haddad, Fatima, Willow, room 128/A, Consistent Carbohydrate, Regular, Thin Liquids.',
      },
    },
    {
      id: 'record',
      part: 'Residents',
      heading: 'Open their record',
      text: 'Tap the card to open the record. The top shows who it is, their unit, room and bed, diet order and allergies, the same as on a computer.\n\nBelow is the Overview, one card under another. Scroll down for the rest.',
      media: {
        image: {
          src: '/help/phones-and-tablets/record.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/record.mp4',
          width: 584,
          height: 1266,
          durationSec: 3,
        },
        alt: 'Haddad, Fatima\'s record on a phone: her name, unit, room and bed, diet order and a Fish allergy at the top, and the Overview below.',
      },
    },
    {
      id: 'sections',
      part: 'Residents',
      heading: 'Move between sections',
      text: 'On a computer the record\'s sections are listed down the left. On a phone they are behind the button at the top that names the section you are in, Overview to start.\n\nTap it and pick a section, such as Diet Order or Meal Pattern.',
      media: {
        image: {
          src: '/help/phones-and-tablets/sections.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/sections.mp4',
          width: 584,
          height: 1266,
          durationSec: 2.5,
        },
        alt: 'The section list open from the Overview button: General with Overview, Primary Info, Physical/Frame/Weights, Attendance, Tray Line, Diet Order, Food Allergies and more, then Meals.',
      },
    },
    {
      id: 'touch',
      part: 'Selective Touch',
      heading: 'Take menu choices with Touch',
      text: 'Tap Touch in the tab bar to open Selective Touch. It works as on a computer: each resident is a card, with the date and meal at the top. The tab bar steps aside to leave room; the X at the top right (Close) leaves Selective Touch.\n\nSee Selective Touch for taking an order step by step.',
      media: {
        image: {
          src: '/help/phones-and-tablets/touch.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/touch.mp4',
          width: 584,
          height: 1266,
          durationSec: 3.1,
        },
        alt: 'Selective Touch on a phone: Search 32 residents, the date and meal button, and resident cards two across, with Add Guest and the next meal at the bottom.',
      },
    },
    {
      id: 'touch-order',
      part: 'Selective Touch',
      heading: 'A resident\'s menu',
      text: 'Tap a resident to see their menu for the meal. Tap the items they choose. Save/Review at the bottom checks the order before you finish it, and Back returns to the residents.',
      media: {
        image: {
          src: '/help/phones-and-tablets/touch-order.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/touch-order.mp4',
          width: 584,
          height: 1266,
          durationSec: 3,
        },
        alt: 'Abernathy, Gloria\'s breakfast menu on a phone: Entree, Cereal, Bread and Beverage choices, with Note, Flag, Regenerate, Save/Review and the next meal at the bottom.',
      },
    },
    {
      id: 'sideways',
      part: 'Selective Touch',
      heading: 'Turn the phone sideways',
      text: 'Turn the phone on its side for a wider menu: two columns of items, with the resident and the meal along the top and the buttons along the bottom. Turn it back whenever you like; nothing is lost.',
      media: {
        image: {
          src: '/help/phones-and-tablets/sideways.webp',
          width: 1266,
          height: 584,
        },
        alt: 'The same breakfast menu with the phone sideways: Entree and Cereal side by side, Bread and Beverage under them, and Note, Flag, Save/Review, Regenerate and Lunch along the bottom.',
      },
    },
    {
      id: 'today',
      part: 'Menus',
      heading: 'Today\'s menu',
      text: 'Tap Menus in the tab bar. Today\'s Menu shows what each meal serves, item by item, from the menu cycle in use. Use the arrows to go to another day, and the meal buttons to change the meal.',
      media: {
        image: {
          src: '/help/phones-and-tablets/today.webp',
          width: 584,
          height: 1266,
        },
        alt: 'Today\'s Menu on a phone: the date, Breakfast, Lunch and Dinner buttons, the menu cycle and day, Swap an item, and the meal\'s Soup, Entree, Fruit and Dessert.',
      },
    },
    {
      id: 'swap',
      part: 'Menus',
      heading: 'Swap one item',
      text: 'Need to change something today, because an item did not arrive? Tap Swap an item. Pick the item to replace, then the item to serve in its place, from another day of the cycle.\n\nThe swap is for that date and meal only. The menu cycle itself is not changed.',
      media: {
        image: {
          src: '/help/phones-and-tablets/swap.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/swap.mp4',
          width: 584,
          height: 1266,
          durationSec: 2.6,
        },
        alt: 'Swap menu on a phone for Fall/Winter Cycle 2026: Replaces one item on today\'s dinner with an item from another day, with the date, the meal and the items to pick from.',
      },
    },
    {
      id: 'editing',
      part: 'Menus',
      heading: 'Menu editing needs a bigger screen',
      text: 'Editing a menu cycle needs room a phone does not have, so it is not offered there. On a phone, the Menu Cycles list has Today\'s menu and Swap only.\n\nIf you open a menu editor from a link on your phone, you see this line instead, with a button to today\'s menu. Use a tablet or a computer for those changes.',
      media: {
        image: {
          src: '/help/phones-and-tablets/editing.webp',
          width: 584,
          height: 1266,
        },
        alt: 'A phone showing Menu editing needs a tablet or computer, with View today\'s menu and Back buttons.',
      },
    },
    {
      id: 'reports',
      part: 'Reports',
      heading: 'Find a report',
      text: 'Tap More, then Reports. Search reports at the top finds one by name, and the buttons under it narrow the list by kind. Your favorites and the reports you ran lately come first.',
      media: {
        image: {
          src: '/help/phones-and-tablets/reports.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/reports.mp4',
          width: 584,
          height: 1266,
          durationSec: 4.1,
        },
        alt: 'Reports on a phone: Search reports, All, Favorites and Meal Service buttons, then Favorites with Food Allergies and Significant Change, and Recently run.',
      },
    },
    {
      id: 'run',
      part: 'Reports',
      heading: 'Run it',
      text: 'Tap the report, choose its options, and tap Generate. A notice says it is on its way, and another when it is ready, with View to open it on screen. It is kept in the Print Queue too, as on a computer. See Running a report.',
      media: {
        image: {
          src: '/help/phones-and-tablets/run.webp',
          width: 584,
          height: 1266,
        },
        clip: {
          src: '/help/phones-and-tablets/run.mp4',
          width: 584,
          height: 1266,
          durationSec: 5,
        },
        alt: 'The Diet Order report on a phone: Report Date, Order By Diet Order picked, and the Generate button at the bottom, with a notice that it was queued and Open Print Queue.',
      },
    },
    {
      id: 'tablet',
      part: 'Tablets and computers',
      heading: 'On a tablet',
      text: 'A tablet shows Dietech Online much as a computer does, with no tab bar. Held upright, the menu folds away behind the button at the top left; on a wide tablet held sideways, it stays down the left side. Buttons and lists are a little larger, for fingers.\n\nEverything is open on a tablet, menu cycle editing included.',
      textOnly: true,
    },
    {
      id: 'computer',
      part: 'Tablets and computers',
      heading: 'What to leave for a computer',
      text: 'A phone is best for looking things up and for quick jobs: a resident\'s record, today\'s menu, a swap, taking menu choices, a report.\n\nLeave long work for a tablet or computer: editing a menu cycle, importing a menu, menu analysis, Auto Item Mods, and big tables you need to see side by side.',
      textOnly: true,
    },
  ],
  tips: [
    'Dietech Online is a website, not something you install. Open it in your phone\'s web browser and sign in as you do on a computer. To get back quickly, add it to your home screen from the browser\'s share or menu button.',
    'The phone\'s own Back gesture or button works too: it goes back one screen, as the browser\'s Back does on a computer.',
    'When you type, the tab bar steps aside so the keyboard has room. It comes back when you finish.',
  ],
  notes: [
    'What you can open is the same on every device: it comes from your role. If a section is missing on your phone, it is missing on your computer too. Ask your administrator.',
    'Menu cycle editing, importing a menu, menu analysis and the other menu tools need a tablet or computer. On a phone the Menus tab shows today\'s menu and lets you swap one item.',
  ],
  related: [
    'getting-around',
    'finding-a-resident',
    'selective-touch',
    'running-a-report',
    'favorites-and-print-queue',
  ],
  recorded: '2026-11-03',
};

export default article;
